import { useEffect, useState } from "react";
import {
  Calendar,
  Phone,
  Mail,
  FileText,
  User,
  CheckCircle2,
  ListTodo,
  Check,
  Clock
} from "lucide-react";
import { motion } from "framer-motion";
import api from "../../../../api";
import { useNotification } from "../../../../context/NotificationContext";
import { Button } from "../../../../components/ui/button";

const iconByType = {
  Appel: <Phone className="w-4 h-4 text-secondary" />,
  Email: <Mail className="w-4 h-4 text-warning-600" />,
  "Rendez-vous": <Calendar className="w-4 h-4 text-success-600" />,
  "Message LinkedIn": <Mail className="w-4 h-4 text-info-600" />,
  "Note interne": <FileText className="w-4 h-4 text-subtle" />,
  Autre: <User className="w-4 h-4 text-subtle" />,
};

const suggestionsClient = [
  { type: "Note interne", note: "Vérifier la conformité des documents KYC" },
  { type: "Email", note: "Envoyer le compte-rendu d'audit patrimonial" },
  { type: "Appel", note: "Point d'étape sur les arbitrages IR/IS" },
];

export default function FamilleStatus({ famille }) {
  const { showToast } = useNotification();
  const [actions, setActions] = useState([]);
  const familleId = famille?.id || famille?._id;

  useEffect(() => {
    const fetchActions = async () => {
      if (!familleId) {
        setActions(suggestionsClient.map((sugg) => ({
          icon: iconByType[sugg.type] || iconByType.Autre,
          titre: sugg.note,
          type: sugg.type,
          canal: "N/A",
          date: null,
          source: "suggestion",
        })));
        return;
      }

      try {
        const res = await api.get(`/interactions/famille/${familleId}`);
        const allInteractions = res.data || [];

        const futures = allInteractions
          .filter((i) => i.prochaineEtape || i.dateProchaineInteraction)
          .sort((a, b) => new Date(a.dateProchaineInteraction) - new Date(b.dateProchaineInteraction))
          .map((item) => ({
            icon: iconByType[item.typeInteraction] || iconByType.Autre,
            titre: item.prochaineEtape || "Étape à préciser",
            date: item.dateProchaineInteraction
              ? new Date(item.dateProchaineInteraction).toLocaleDateString("fr-FR")
              : null,
            canal: item.canal,
            type: item.typeInteraction,
            source: "futur",
          }));

        const suggestionsDéjàRéalisées = new Set(
          allInteractions.map((i) => `${i.typeInteraction}_${i.contenu?.trim()}`)
        );

        const suggestions = suggestionsClient
          .filter((sugg) => !suggestionsDéjàRéalisées.has(`${sugg.type}_${sugg.note.trim()}`))
          .map((sugg) => ({
            icon: iconByType[sugg.type] || iconByType.Autre,
            titre: sugg.note,
            type: sugg.type,
            canal: "N/A",
            date: null,
            source: "suggestion",
          }));

        setActions([...futures, ...suggestions]);
      } catch (err) {
        console.error("Erreur récupération interactions famille :", err);
        setActions([]);
      }
    };

    fetchActions();
  }, [familleId]);

  const handleMarkAsDone = async (action) => {
    try {
      await api.post("/interactions", {
        typeInteraction: action.type,
        canal: action.canal === "N/A" ? "Autre" : action.canal,
        date: new Date(),
        contenu: action.titre,
        statut: famille?.statut || "Client",
        referent: famille?.referent || "Non assigné",
        prospectId: familleId,
        commentaireInterne: "Validé depuis FamilleStatus",
      });

      showToast("Tâche validée et consignée dans l'historique !", "success");
      setActions(prev => prev.filter(a => a.titre !== action.titre));
    } catch (err) {
      console.error("Erreur ajout interaction famille :", err);
      showToast("Erreur lors de l'enregistrement", "error");
    }
  };

  return (
    <div className="workspace-card p-5 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm flex flex-col w-full">
      <div className="flex items-center justify-between pb-3 border-b border-workspace mb-3">
        <div className="flex items-center gap-2">
          <ListTodo className="w-5 h-5 text-secondary" />
          <h2 className="text-base font-bold text-primary">Actions & Prochaines Étapes</h2>
        </div>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-surface-muted text-subtle">
          {actions.length} tâche(s)
        </span>
      </div>

      {actions.length > 0 ? (
        <div className="space-y-2.5 w-full">
          {actions.slice(0, 3).map((action, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col gap-2 p-3 bg-surface-muted/30 rounded-lg border border-workspace hover:border-workspace transition-colors"
            >
              <div className="flex items-start gap-2.5">
                <div className="mt-0.5 p-1.5 rounded-md bg-surface border border-workspace shrink-0">
                  {action.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-primary break-words">
                    {action.titre}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-subtle">
                    {action.date && (
                      <span className="flex items-center gap-1 font-medium text-secondary">
                        <Clock className="w-3 h-3" />
                        {action.date}
                      </span>
                    )}
                    <span>({action.type}{action.canal !== "N/A" && ` • ${action.canal}`})</span>
                  </div>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="self-end text-xs text-success-700 hover:text-success-800 hover:bg-success-50 h-7 px-2.5 flex items-center gap-1"
                onClick={() => handleMarkAsDone(action)}
              >
                <Check className="w-3.5 h-3.5" />
                Marquer comme faite
              </Button>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="p-6 text-center text-xs text-subtle italic flex flex-col items-center gap-2">
          <CheckCircle2 className="w-8 h-8 text-success-500 opacity-60" />
          <span>Toutes les actions de suivi sont à jour.</span>
        </div>
      )}
    </div>
  );
}
